import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PieChart, Pie, Cell } from 'recharts';
import { WEIGHTING } from '../Data/weighting';
import { CISCO_CRITERIA } from '../Data/Criteria_Cisco';
import { PALO_ALTO_CRITERIA } from '../Data/Criteria_Palo_Alto';
import { ARUBA_CRITERIA } from '../Data/Criteria_Aruba';
import { JUNIPER_CRITERIA } from '../Data/Criteria_Juniper';
import { AIRESPRING_CRITERIA } from '../Data/Criteria_Airespring';

// Harvey Ball Components
const ComplianceHarveyBall = ({ percentage }) => {
  const rating = Math.round((percentage / 100) * 4);
  const data = new Array(4).fill(null).map((_, index) => ({
    value: 1,
    filled: index < rating,
  }));

  return (
    <PieChart width={40} height={40}>
      <Pie
        data={data}
        dataKey="value"
        cx="50%"
        cy="50%"
        innerRadius={0}
        outerRadius={15}
        startAngle={-90}
        endAngle={270}
      >
        {data.map((entry, index) => (
          <Cell
            key={`cell-${index}`}
            fill={entry.filled ? '#1976d2' : '#f5f5f5'}
            stroke="#1976d2"
            strokeWidth={0}
          />
        ))}
      </Pie>
    </PieChart>
  );
};

const RatingHarveyBall = ({ rating }) => {
  const quartersMap = {
    0: 0,
    1: 1,
    2: 2,
    3: 4,
  };

  const quarters = quartersMap[Math.round(rating)] || 0;
  const data = new Array(4).fill(null).map((_, index) => ({
    value: 1,
    filled: index < quarters,
  }));

  return (
    <PieChart width={40} height={40}>
      <Pie
        data={data}
        dataKey="value"
        cx="50%"
        cy="50%"
        innerRadius={0}
        outerRadius={15}
        startAngle={-90}
        endAngle={270}
      >
        {data.map((entry, index) => (
          <Cell
            key={`cell-${index}`}
            fill={entry.filled ? '#4caf50' : '#f5f5f5'}
            stroke="#4caf50"
            strokeWidth={0}
          />
        ))}
      </Pie>
    </PieChart>
  );
};

const TechnologyAnalysis = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [analysisData, setAnalysisData] = useState(null);
  const [ratingAnalysisData, setRatingAnalysisData] = useState(null);
  const [expandedCriteria, setExpandedCriteria] = useState({});

  const VENDORS = ['JUNIPER', 'ARUBA', 'PALO_ALTO', 'CISCO', 'AIRESPRING'];
  const VENDOR_DATA_MAP = {
    CISCO: CISCO_CRITERIA.CISCO,
    PALO_ALTO: PALO_ALTO_CRITERIA.PALO_ALTO,
    ARUBA: ARUBA_CRITERIA.ARUBA,
    JUNIPER: JUNIPER_CRITERIA.JUNIPER,
    AIRESPRING: AIRESPRING_CRITERIA.AIRESPRING,
  };

  // Calculation functions
  const calculateCriterionScore = (vendorData, criterion) => {
    if (!vendorData || !Array.isArray(vendorData)) {
      return { percentage: 0, yesCount: 0, totalCount: 0 };
    }

    const relevantQuestions = vendorData.filter(
      (item) => item.Criteria === criterion
    );

    if (relevantQuestions.length === 0) {
      return { percentage: 0, yesCount: 0, totalCount: 0 };
    }

    const yesQuestions = relevantQuestions.filter((item) => item.Check === 'Yes');
    const totalCount = relevantQuestions.length;
    const percentage = (yesQuestions.length / totalCount) * 100;

    return {
      percentage,
      yesCount: yesQuestions.length,
      totalCount,
      questions: relevantQuestions,
    };
  };

  const calculateRatingScore = (vendorData, criterion) => {
    if (!vendorData || !Array.isArray(vendorData)) {
      return { averageRating: 0, questions: [] };
    }

    const relevantQuestions = vendorData.filter(
      (item) => item.Criteria === criterion && item.Rating && item.Rating <= 3
    );

    if (relevantQuestions.length === 0) {
      return { averageRating: 0, questions: [] };
    }

    const totalRating = relevantQuestions.reduce(
      (sum, item) => sum + (item.Rating || 0),
      0
    );
    const averageRating = totalRating / relevantQuestions.length;
    const percentage = (averageRating / 3) * 100;

    return {
      averageRating,
      percentage,
      questions: relevantQuestions,
    };
  };

  const calculateOverallScore = (vendorData, useRating = false) => {
    let weightedScore = 0;
    let totalWeight = 0;

    Object.entries(WEIGHTING.Criteria).forEach(([criterion, weight]) => {
      if (useRating) {
        const { percentage } = calculateRatingScore(vendorData, criterion);
        weightedScore += (percentage / 100) * weight;
      } else {
        const { percentage } = calculateCriterionScore(vendorData, criterion);
        weightedScore += (percentage / 100) * weight;
      }
      totalWeight += weight;
    });

    return totalWeight > 0 ? (weightedScore / totalWeight) * 100 : 0;
  };

  // Data processing effect
  useEffect(() => {
    try {
      const processedData = VENDORS.map((vendor) => {
        const vendorData = VENDOR_DATA_MAP[vendor];

        if (!vendorData) {
          console.error(`No data found for vendor: ${vendor}`);
          return null;
        }

        const criteriaScores = Object.entries(WEIGHTING.Criteria)
          .filter(([_, weight]) => weight > 0)
          .map(([criterion, weight]) => ({
            name: criterion,
            weight,
            ...calculateCriterionScore(vendorData, criterion),
          }));

        return {
          vendor,
          criteria: criteriaScores,
          overallScore: calculateOverallScore(vendorData),
        };
      }).filter(Boolean);

      const processedRatingData = VENDORS.map((vendor) => {
        const vendorData = VENDOR_DATA_MAP[vendor];

        if (!vendorData) {
          return null;
        }

        const criteriaRatings = Object.entries(WEIGHTING.Criteria)
          .filter(([_, weight]) => weight > 0)
          .map(([criterion, weight]) => ({
            name: criterion,
            weight,
            ...calculateRatingScore(vendorData, criterion),
          }));

        return {
          vendor,
          criteria: criteriaRatings,
          overallScore: calculateOverallScore(vendorData, true),
        };
      }).filter(Boolean);

      setAnalysisData(processedData);
      setRatingAnalysisData(processedRatingData);
    } catch (error) {
      console.error('Error processing technology analysis:', error);
      setAnalysisData([]);
      setRatingAnalysisData([]);
    }
  }, []);

  const handleCriteriaToggle = (criterion) => {
    setExpandedCriteria((prev) => ({
      ...prev,
      [criterion]: !prev[criterion],
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const OverallScoreSection = ({ data, useRating }) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `40px 300px ${VENDORS.map(() => '1fr').join(' ')}`,
        gap: 2,
        px: 2,
        py: 2,
        backgroundColor: 'primary.light',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box />
      <Box sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
        Overall Score
      </Box>
      {data.map((vendor, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {useRating ? (
            <>
              <RatingHarveyBall rating={(vendor.overallScore / 100) * 3} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                {vendor.overallScore.toFixed(1)}%
              </Typography>
            </>
          ) : (
            <>
              <ComplianceHarveyBall percentage={vendor.overallScore} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                {vendor.overallScore.toFixed(1)}%
              </Typography>
            </>
          )}
        </Box>
      ))}
    </Box>
  );

  const HeaderRow = ({ data }) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `40px 300px ${VENDORS.map(() => '1fr').join(' ')}`,
        gap: 2,
        px: 2,
        py: 1,
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box />
      <Box sx={{ fontWeight: 'bold' }}>Criteria</Box>
      {data.map((vendor, idx) => (
        <Box
          key={idx}
          sx={{
            textAlign: 'center',
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
        >
          {vendor.vendor.replace('_', ' ')}
        </Box>
      ))}
    </Box>
  );

  const renderCriteriaContent = (data, useRating) => {
    const allCriteria = Object.keys(WEIGHTING.Criteria)
      .filter((criterion) => WEIGHTING.Criteria[criterion] > 0)
      .sort();

    return allCriteria.map((criterion, cidx) => {
      const isExpanded = expandedCriteria[criterion];

      return (
        <Box key={cidx}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `40px 300px ${VENDORS.map(() => '1fr').join(' ')}`,
              gap: 2,
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              alignItems: 'center',
            }}
          >
            <IconButton
              size="small"
              onClick={() => handleCriteriaToggle(criterion)}
            >
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {criterion}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {WEIGHTING.Criteria[criterion]}%
              </Typography>
            </Box>

            {data.map((vendor, vidx) => {
              const criterionData = vendor.criteria.find(
                (c) => c.name === criterion
              );
              return (
                <Box
                  key={vidx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  {useRating ? (
                    <>
                      <RatingHarveyBall
                        rating={criterionData?.averageRating || 0}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {criterionData?.averageRating?.toFixed(1) || '0'}/3
                      </Typography>
                    </>
                  ) : (
                    <>
                      <ComplianceHarveyBall
                        percentage={criterionData?.percentage || 0}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {criterionData?.yesCount || 0}/{criterionData?.totalCount || 0}
                      </Typography>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>

          {isExpanded && (
            <Box
              sx={{
                gridColumn: '1 / -1',
                p: 2,
                backgroundColor: 'grey.100',
              }}
            >
              {data[0].criteria
                .find((c) => c.name === criterion)
                ?.questions.map((question, qidx) => (
                  <Box
                    key={qidx}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `300px repeat(${VENDORS.length}, 1fr)`,
                      gap: 2,
                      mb: 2,
                      pb: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2">{question.Questions}</Typography>
                    {VENDORS.map((vendor) => {
                      const vendorQuestion = VENDOR_DATA_MAP[vendor].find(
                        (q) =>
                          q.Criteria === criterion &&
                          q.Questions === question.Questions
                      );
                      return (
                        <Tooltip
                          key={vendor}
                          title={vendorQuestion?.Notes || ''}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: 'center',
                              color: useRating
                                ? theme.palette.success.main
                                : vendorQuestion?.Check === 'Yes'
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                            }}
                          >
                            {useRating
                              ? `${vendorQuestion?.Rating || 0}/3`
                              : vendorQuestion?.Check || 'N/A'}
                          </Typography>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      );
    });
  };

  if (!analysisData || analysisData.length === 0) {
    return (
      <Box sx={{ p: 2, width: '100%' }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">No vendor data available</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Paper 
        sx={{ 
          width: '100%', 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Fixed Header with Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="technology analysis tabs"
            sx={{
              '& .MuiTab-root': {
                minWidth: 120,
                px: 3,
              },
            }}
          >
            <Tab label="Compliance Analysis" />
            <Tab label="Rating Analysis" />
          </Tabs>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Fixed Headers */}
          <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper' }}>
            <HeaderRow data={activeTab === 0 ? analysisData : ratingAnalysisData} />
            <OverallScoreSection 
              data={activeTab === 0 ? analysisData : ratingAnalysisData} 
              useRating={activeTab === 1} 
            />
          </Box>

          {/* Scrollable Content */}
          <Box 
            sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
                '&:hover': {
                  background: '#555',
                },
              },
            }}
          >
            {activeTab === 0 && renderCriteriaContent(analysisData, false)}
            {activeTab === 1 && renderCriteriaContent(ratingAnalysisData, true)}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default TechnologyAnalysis;